/**
 * spa-nav.js
 * Intercepts same-origin link clicks and swaps only #MainContent,
 * keeping the header, mega menu, and footer fully static.
 *
 * Before swapping content, any new section stylesheets from the incoming page
 * are pre-loaded into <head> so custom elements (e.g. modal-dialog) never
 * briefly render without their hiding/layout CSS applied.
 */
(function () {
  'use strict';

  // Disable entirely inside the Shopify theme editor — section drag-and-drop
  // and live preview need normal full-page navigation to work.
  if (window.Shopify && window.Shopify.designMode) return;

  const MAIN_ID = 'MainContent';
  let isNavigating = false;

  // ── Helpers ──────────────────────────────────────────────────────────────

  function shouldSkip(href) {
    let url;
    try { url = new URL(href, window.location.href); } catch (_) { return true; }

    // Different origin — always a full navigation.
    if (url.origin !== window.location.origin) return true;

    // Hash-only on the same path — let the browser scroll.
    if (url.pathname === window.location.pathname && url.hash && !url.search) return true;

    // Shopify system paths that need full page context.
    if (/^\/(cart|checkout|a\/|admin|account)/.test(url.pathname)) return true;

    // CDN / asset downloads.
    if (url.pathname.startsWith('/cdn/')) return true;

    return false;
  }

  /**
   * Scripts injected via innerHTML are inert — we must clone and re-insert
   * them to actually execute.
   */
  function reRunScripts(container) {
    // External libraries (facets.js, etc.) declare top-level classes and define
    // custom elements. They are already loaded from the first page load and
    // persist across the content swap, so re-executing them throws
    // "Identifier '…' has already been declared" and re-registers custom
    // elements. Skip any <script src> whose URL is already loaded outside the
    // swapped container; only (re)run inline scripts and genuinely new externals.
    var loaded = new Set();
    document.querySelectorAll('script[src]').forEach(function (s) {
      if (!container.contains(s)) loaded.add(s.src);
    });

    container.querySelectorAll('script').forEach(function (old) {
      var src = old.getAttribute('src');
      if (src) {
        var abs = new URL(src, window.location.href).href;
        if (loaded.has(abs)) { if (old.parentNode) old.parentNode.removeChild(old); return; }
        loaded.add(abs);
      }
      var fresh = document.createElement('script');
      Array.from(old.attributes).forEach(function (a) { fresh.setAttribute(a.name, a.value); });
      fresh.textContent = old.textContent;
      old.parentNode.replaceChild(fresh, old);
    });
  }

  // ── Core navigate ─────────────────────────────────────────────────────────

  async function navigate(url, push) {
    if (isNavigating) return;
    isNavigating = true;

    var main = document.getElementById(MAIN_ID);
    if (!main) { window.location.href = url; isNavigating = false; return; }

    main.style.opacity = '0.4';
    main.style.transition = 'opacity 0.12s ease';

    try {
      var res = await fetch(url, { credentials: 'same-origin' });

      if (!res.ok) { window.location.href = url; return; }

      var html = await res.text();
      var doc  = new DOMParser().parseFromString(html, 'text/html');
      var newMain = doc.getElementById(MAIN_ID);

      if (!newMain) { window.location.href = url; return; }

      // Update URL + title.
      if (push) history.pushState({ spaUrl: url }, doc.title, url);
      document.title = doc.title;

      // Pre-load any section stylesheets that are not yet in the document.
      // This prevents the race where modal custom-elements move themselves to
      // document.body before their hiding CSS (position:fixed; visibility:hidden)
      // has been parsed, causing product images to flash as giant visible blocks
      // below the footer.
      var alreadyLoaded = new Set(
        Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(function (l) { return l.href; })
      );
      var incomingLinks = Array.from(newMain.querySelectorAll('link[rel="stylesheet"]'));
      var toPreload = incomingLinks.filter(function (l) {
        var href = new URL(l.getAttribute('href') || '', window.location.href).href;
        return href && !alreadyLoaded.has(href);
      });

      if (toPreload.length > 0) {
        await Promise.all(toPreload.map(function (l) {
          return new Promise(function (resolve) {
            var link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = new URL(l.getAttribute('href'), window.location.href).href;
            link.addEventListener('load',  resolve, { once: true });
            link.addEventListener('error', resolve, { once: true });
            document.head.appendChild(link);
          });
        }));
      }

      // Swap content.
      main.innerHTML = newMain.innerHTML;
      reRunScripts(main);

      // Scroll to top + move focus for accessibility.
      window.scrollTo({ top: 0, behavior: 'instant' });
      main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });

    } catch (_) {
      window.location.href = url;
    } finally {
      isNavigating = false;
      main = document.getElementById(MAIN_ID);
      if (main) { main.style.opacity = ''; main.style.transition = ''; }
    }
  }

  // ── Event listeners ───────────────────────────────────────────────────────

  // Intercept clicks.
  document.addEventListener('click', function (e) {
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

    var link = e.target.closest('a[href]');
    if (!link) return;
    if (link.target && link.target !== '_self') return;
    if (link.hasAttribute('download')) return;

    var href = link.getAttribute('href');
    if (!href || shouldSkip(href)) return;

    e.preventDefault();
    navigate(new URL(href, window.location.href).href, true);
  });

  // Back / forward.
  window.addEventListener('popstate', function (e) {
    navigate((e.state && e.state.spaUrl) || window.location.href, false);
  });

  // Seed initial history entry so the very first popstate has a spaUrl.
  history.replaceState({ spaUrl: window.location.href }, document.title, window.location.href);

})();
